import { createSlice } from '@reduxjs/toolkit';
import {
  getOrderSheetData,
  getOrderingFacilitiesData,
  getOrderingCategoriesData,
  getCartTotalData,
  addToCartApi,
  getAllCartsItemsCountApi
} from '../../api/order-sheet';
import { getBudgetsData } from '../../api/orders';

const initialState = {
  isLoading: false,
  error: null,
  data: {
    allCartsItemsCount: null,
    cartItems: [],
    agoraCategory: {},
    budgets: null,
    budgetsMessage: null,
    budgetPpdInfo: {},
    cartInfo: [],
    facilities: [],
    items: [],
    added: 0,
    categories: [],
    selectedFacilityId: null,
    currFacilityId: null,
    hasNoItems: false,
    hasNoBudget: false,
  },
};

const slice = createSlice({
  name: 'orderSheet',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    setProductsFiltered(state, action) {
      state.productsFiltered = action.payload;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getDataSuccess(state, action) {
      state.isLoading = false;
      state.data = { ...state.data, ...action.payload };
    },

    getBudgetsSuccess(state, action) {
      state.isLoading = false;
      state.data = { ...state.data, budgets: action.payload?.budgets, budgetsMessage: action.payload?.message };
    },

    setBudgetPpdInfo(state, action) {
      const { catId, info } = action.payload;
      if (catId == null) state.data.budgetPpdInfo = {};
      else state.data.budgetPpdInfo = { ...state.data.budgetPpdInfo, [catId]: info };
    },

    getCartTotalSuccess(state, action) {
      state.isLoading = false;
      state.data = { ...state.data, cartInfo: action.payload };
    },

    getOrderSheetSuccess(state, action) {
      const hasNoItems = action.payload.items.length === 0;
      const hasNoBudget = action.payload.budgets.length === 0;
      state.data = { ...state.data, ...action.payload, hasNoItems, hasNoBudget };
      state.isLoading = false;
    },

    updateReduxCatalogItem(state, action) {
      const { id, field, value } = action.payload;
      const { items } = state.data;
      const item = items?.find((item) => item.id === id);

      if (item.productDetails[0]) {
        item.productDetails[0][field] = value;
      }
      state.data = { ...state.data, items };
      state.isLoading = false;
    },

    updateAddedAmount(state, action) {
      state.data.added = action.payload;
    },

    clearAdded(state) {
      state.data.added = 0;
    },

    getAllCartsItemsCountSuccess(state, action) {
      state.data.allCartsItemsCount = action.payload;
    },

    updateAllCartsItemsCount(state, action) {
      state.data.allCartsItemsCount += action.payload;
    },

    removeAllCartInfoItems(state) {
      state.data.cartInfo = { ...state.data.cartInfo, cartItems: [] };
    },

    updateCartInfoItems(state, action) {
      state.data.cartInfo = {...state.data.cartInfo, cartItems: action.payload};
    }
  }
});

export function getOrderSheet(categoryId, facilityId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getOrderSheetData(categoryId, facilityId);
      response.data.items.forEach((item) => { item.orderCategoryId = response.data.orderCategoryId; });
      response.data.items = response.data.items.sort((a, b) => a.displaySubcategory.name < b.displaySubcategory.name ? -1 : 1);
      const categoryCartInfo = response.data.cartInfo?.cartItems?.find(c => c.categoryId.toString() === categoryId.toString());
      response.data.cart = categoryCartInfo?.items.map(cartItem => {
        const itemData = response.data.items?.find(i => i.productDetails?.some(pd => pd.id.toString() === cartItem.itemId));
        if (!itemData) return null;
        return { ...itemData, quantity: cartItem.quantity, price: itemData.productDetails[0].price};
      }).filter(item => item) ?? [];
      const mergeFacilityIDToResponse = { ...response.data, currFacilityId: facilityId };
      dispatch(slice.actions.getOrderSheetSuccess(mergeFacilityIDToResponse));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getOrderingFacilities() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getOrderingFacilitiesData();
      dispatch(slice.actions.getDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getOrderingCategories(facilityId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getOrderingCategoriesData(facilityId);
      dispatch(slice.actions.getDataSuccess({ categories: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setAdditionToCartBadge(added) {
  return (dispatch) => {
    dispatch(slice.actions.updateAddedAmount(added));
    setTimeout(() => {
      dispatch(slice.actions.clearAdded());
    }, 2000);
  };
}

export function getCartTotal(facilityId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getCartTotalData(facilityId);
      dispatch(slice.actions.getDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addItemsToCart(items, facilityId, categoryId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await addToCartApi(items, facilityId, categoryId);
      dispatch(slice.actions.updateAllCartsItemsCount(items?.reduce((acc, item) => acc + item.quantity, 0) || 0))
      dispatch(slice.actions.getCartTotalSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBudgets({ facilityId, includeUnapproved, useCurrentCensus, orderNumber = 0, categories, tab = 0, budgetDate = null }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getBudgetsData({
        facilityId,
        includeUnapproved,
        useCurrentCensus,
        orderNumber,
        categories,
        tab,
        budgetDate
      });
      dispatch(slice.actions.getBudgetsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// updateCartItemInRedux
export function updateCatalogItemInRedux(id, field, value) {
  return (dispatch) => {
    try {
      dispatch(slice.actions.updateReduxCatalogItem({ id, field, value }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllCartsItemsCount() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getAllCartsItemsCountApi();
      dispatch(slice.actions.getAllCartsItemsCountSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateAllCartsItemsCount(change) {
  return async (dispatch) => { dispatch(slice.actions.updateAllCartsItemsCount(change)); };
}

export function removeAllCartInfoItems() {
  return async (dispatch) =>  dispatch(slice.actions.removeAllCartInfoItems());
}

export function updateCartInfoItems(data) {
  return async (dispatch) =>  dispatch(slice.actions.updateCartInfoItems(data));
}

export function setBudgetPpdInfo(catId, info) {
  return async (dispatch) => dispatch(slice.actions.setBudgetPpdInfo({ catId, info }));
}

export default slice.reducer;
